import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSupportChat } from '../contexts/SupportChatContext';

const DRAG_THRESHOLD = 5;

const BOTTOM_NAV_HEIGHT = 64; // Approximate height of mobile bottom nav bar
const MARGIN = 12;

function getDefaultSupportPosition(): { right: number; bottom: number } {
  if (typeof window === 'undefined') return { right: MARGIN, bottom: MARGIN };
  const isMobile = window.innerWidth < 1024; // lg breakpoint
  const right = MARGIN;
  const bottom = isMobile
    ? BOTTOM_NAV_HEIGHT + MARGIN // Right-top of bottom nav bar
    : MARGIN; // Bottom-right corner on desktop
  return { right, bottom };
}

function clampSupportPosition(right: number, bottom: number): { right: number; bottom: number } {
  if (typeof window === 'undefined') return { right, bottom };
  const minMargin = 8;
  return {
    right: Math.max(minMargin, right),
    bottom: Math.max(minMargin, Math.min(window.innerHeight - 60, bottom)),
  };
}

const CustomerSupportChat: React.FC = () => {
  const {
    conversation,
    messages,
    loading,
    error,
    isConnected,
    unreadCount,
    sendMessage,
    loadConversation,
    markAsRead,
  } = useSupportChat();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [position, setPosition] = useState<{ right: number; bottom: number }>(getDefaultSupportPosition);
  const dragRef = useRef<{ startX: number; startY: number; posRight: number; posBottom: number; moved: boolean } | null>(null);
  const lastPositionRef = useRef(position);
  const justDraggedRef = useRef(false);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const target = e.currentTarget;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      posRight: position.right,
      posBottom: position.bottom,
      moved: false,
    };
    justDraggedRef.current = false;
    if (target.setPointerCapture) {
      target.setPointerCapture(e.pointerId);
    }
    const onMove = (e2: Event) => {
      const pe = e2 as PointerEvent;
      pe.preventDefault();
      if (!dragRef.current) return;
      const dx = pe.clientX - dragRef.current.startX;
      const dy = pe.clientY - dragRef.current.startY;
      if (!dragRef.current.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        dragRef.current.moved = true;
        justDraggedRef.current = true;
      }
      if (dragRef.current.moved) {
        // right decreases when moving right (dx > 0), bottom decreases when moving down (dy > 0)
        const next = clampSupportPosition(dragRef.current.posRight - dx, dragRef.current.posBottom - dy);
        lastPositionRef.current = next;
        setPosition(next);
      }
    };
    const onUp = (e2: Event) => {
      const pe = e2 as PointerEvent;
      pe.preventDefault();
      if (target.releasePointerCapture && pe.pointerId !== undefined) {
        try {
          target.releasePointerCapture(pe.pointerId);
        } catch {
          // ignore
        }
      }
      // Position resets to default on page refresh - no persistence
      target.removeEventListener('pointermove', onMove as EventListener);
      target.removeEventListener('pointerup', onUp as EventListener);
      target.removeEventListener('pointercancel', onUp as EventListener);
      window.removeEventListener('pointermove', onMove as EventListener);
      window.removeEventListener('pointerup', onUp as EventListener);
      window.removeEventListener('pointercancel', onUp as EventListener);
      dragRef.current = null;
    };
    target.addEventListener('pointermove', onMove as EventListener);
    target.addEventListener('pointerup', onUp as EventListener);
    target.addEventListener('pointercancel', onUp as EventListener);
    window.addEventListener('pointermove', onMove as EventListener);
    window.addEventListener('pointerup', onUp as EventListener);
    window.addEventListener('pointercancel', onUp as EventListener);
  }, [position.right, position.bottom]);

  const handleButtonClick = useCallback(() => {
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    setIsOpen(true);
    setIsMinimized(false);
    if (!conversation) {
      loadConversation().catch((err: unknown) => {
        console.error('Failed to load conversation when opening chat:', err);
      });
    }
  }, [conversation, loadConversation]);

  useEffect(() => {
    const onResize = () => setPosition(getDefaultSupportPosition());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isOpen && conversation) {
      markAsRead();
    }
  }, [isOpen, conversation, markAsRead]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim() || loading) return;

    const message = messageInput;
    setMessageInput('');
    await sendMessage(message);
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  const shouldShowDate = (currentMessage: any, previousMessage: any) => {
    if (!previousMessage) return true;
    const currentDate = new Date(currentMessage.created_at).toDateString();
    const previousDate = new Date(previousMessage.created_at).toDateString();
    return currentDate !== previousDate;
  };

  return (
    <>
      {/* Chat Button - draggable, position saved to localStorage */}
      {!isOpen && (
        <button
          type="button"
          onClick={handleButtonClick}
          onPointerDown={handlePointerDown}
          style={{ right: position.right, bottom: position.bottom, touchAction: 'none' }}
          className="fixed z-50 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-full p-3 sm:p-4 shadow-lg transition-none flex items-center justify-center select-none cursor-grab active:cursor-grabbing"
          aria-label="Open customer support chat"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-[10px] sm:text-xs font-semibold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Window - full screen on mobile (above header/bottom nav); floating on desktop */}
      {isOpen && (
        <div
          className={`fixed z-[1001] sm:z-50 bg-dark-800 shadow-2xl transition-all duration-300 flex flex-col border border-dark-700
            ${
              isMinimized 
                ? 'bottom-4 right-4 sm:bottom-6 sm:right-6 h-16 w-64 sm:w-80 rounded-lg' 
                : 'inset-0 sm:inset-auto sm:bottom-6 sm:right-6 h-full sm:h-[600px] w-full sm:w-96 rounded-none sm:rounded-lg'
            }
          `}
        >
          {/* Header */}
          <div className="bg-green-600 text-white p-3 sm:p-4 rounded-t-lg sm:rounded-t-lg flex items-center justify-between flex-shrink-0">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isConnected ? 'bg-green-300' : 'bg-red-300'}`} />
              <h3 className="font-semibold text-sm sm:text-base truncate">Customer Support</h3>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="hover:bg-green-700 active:bg-green-800 rounded p-1.5 sm:p-1 transition-colors touch-manipulation"
                aria-label={isMinimized ? 'Maximize' : 'Minimize'}
              >
                <svg
                  className="w-5 h-5 sm:w-4 sm:h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={isMinimized ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                  />
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-green-700 active:bg-green-800 rounded p-1.5 sm:p-1 transition-colors touch-manipulation"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5 sm:w-4 sm:h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 custom-scrollbar">
                {loading && !conversation ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-300 text-sm sm:text-base">Loading...</div>
                  </div>
                ) : error ? (
                  <div className="text-red-400 text-xs sm:text-sm p-2">{error}</div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-300 text-xs sm:text-sm px-4">
                    <svg
                      className="w-10 h-10 sm:w-12 sm:h-12 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <p className="text-center">Start a conversation with our support team</p>
                  </div>
                ) : (
                  messages.map((message, index) => {
                    const showDate = shouldShowDate(message, messages[index - 1]);
                    const isUser = message.sender_type === 'user';

                    return (
                      <React.Fragment key={message.id}>
                        {showDate && (
                          <div className="text-center text-xs text-gray-500 my-3 sm:my-4">
                            {formatDate(message.created_at)}
                          </div>
                        )}
                        <div
                          className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[85%] sm:max-w-[75%] rounded-lg p-2.5 sm:p-3 ${
                              isUser
                                ? 'bg-green-600 text-white'
                                : 'bg-dark-700 text-gray-200'
                            }`}
                          >
                            {!isUser && (
                              <div className="text-xs font-semibold mb-1 opacity-80">
                                {message.sender_name}
                              </div>
                            )}
                            <div className="text-xs sm:text-sm whitespace-pre-wrap break-words">
                              {message.message}
                            </div>
                            <div
                              className={`text-[10px] sm:text-xs mt-1 ${
                                isUser ? 'text-green-100' : 'text-gray-300'
                              }`}
                            >
                              {formatTime(message.created_at)}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="border-t border-dark-700 p-3 sm:p-4 flex-shrink-0">
                <form onSubmit={handleSendMessage} className="flex space-x-2">
                  <textarea
                    ref={inputRef}
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                    placeholder="Type your message..."
                    className="flex-1 bg-dark-700 text-white rounded-lg px-3 sm:px-4 py-2 text-sm sm:text-base resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                    rows={window.innerWidth < 640 ? 2 : 2}
                    disabled={loading || !isConnected}
                  />
                  <button
                    type="submit"
                    disabled={!messageInput.trim() || loading || !isConnected}
                    className="bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg px-3 sm:px-4 py-2 transition-colors touch-manipulation flex-shrink-0"
                    aria-label="Send message"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  </button>
                </form>
                {!isConnected && (
                  <p className="text-xs text-yellow-400 mt-2 text-center sm:text-left">
                    Connecting to support...
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default CustomerSupportChat;

